import { Box, Card } from '@mui/material';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from '../../../../ui-component/FormikFormLibrary/FormikTypes';
import { Dispatch, SetStateAction } from 'react';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';

type AddNewTownshipProps = {
  setTownshipAction: Dispatch<SetStateAction<string>>;
  townshipAction: any;
  handleApiCall: () => void;
};

const AddTownship: React.FC<AddNewTownshipProps> = ({
  setTownshipAction,
  townshipAction,
  handleApiCall
}) => {
  const allFields: Fields[] = [
    {
      fieldName: 'TownshipName',
      fieldLabel: 'Township Name',
      fieldType: 'text',
      required: true,
      heading:
        townshipAction === 'Add'
          ? 'Add New Township'
          : 'Update Township Details',
      disabled: townshipAction !== 'Add',
      initialValue: townshipAction?.TownshipName || ''
    },
    {
      fieldName: 'State',
      fieldLabel: 'State',
      fieldType: 'text',
      required: true,
      disabled: townshipAction !== 'Add',
      initialValue: townshipAction?.State || ''
    },
    {
      fieldName: 'County',
      fieldLabel: 'County',
      fieldType: 'text',
      required: true,
      disabled: townshipAction !== 'Add',
      initialValue: townshipAction?.County || ''
    },
    {
      fieldName: 'Address',
      fieldLabel: 'Address',
      fieldType: 'text',
      required: true,
      initialValue: townshipAction?.Address || ''
    },
    {
      fieldName: 'City',
      fieldLabel: 'City',
      fieldType: 'text',
      required: true,
      disabled: townshipAction !== 'Add',
      initialValue: townshipAction?.City || ''
    },
    {
      fieldName: 'ZipCode',
      fieldLabel: 'Zip Code',
      fieldType: 'text',
      required: true,
      disabled: townshipAction !== 'Add',
      initialValue: townshipAction?.ZipCode || ''
    },
    {
      fieldName: 'ContactName',
      fieldLabel: 'Contact Name',
      fieldType: 'text',
      required: true,
      initialValue: townshipAction?.ContactName || ''
    },
    {
      fieldName: 'ContactEmail',
      fieldLabel: 'Contact Email',
      fieldType: 'email',
      required: true,
      initialValue: townshipAction?.ContactEmail || ''
    },
    {
      fieldName: 'ContactPhoneNumber',
      fieldLabel: 'Contact Phone Number',
      fieldType: 'mobile',
      required: true,
      initialValue: townshipAction?.ContactPhoneNumber || ''
    },
    {
      fieldName: 'CreatedDate',
      fieldLabel: 'Created Date',
      fieldType: 'date',
      disabled: true,
      initialValue: townshipAction?.CreateDate || new Date().toDateString()
    },
    {
      fieldName: 'TownshipTagLine',
      fieldLabel: 'Township Tag Line',
      fieldType: 'note',
      required: false,
      placeHolder: 'Type your text here (255 characters max)',
      initialValue: townshipAction?.TownshipTagLine || ''
    },
    {
      fieldName: 'TownshipDescription',
      fieldLabel: 'Township Description',
      fieldType: 'note',
      required: false,
      placeHolder: 'Type your text here (255 characters max)',
      initialValue: townshipAction?.TownshipDescription || ''
    }
  ];

  // Status field for update township
  const statusField: Fields[] = [
    {
      fieldName: 'Status',
      fieldLabel: 'Status',
      fieldType: 'select',
      options: ['Active', 'In Active'],
      required: true,
      initialValue: townshipAction?.Status || ''
    }
  ];

  // Admin fields for add township
  const adminFields: Fields[] = [
    {
      fieldName: 'AdminFirstName',
      fieldLabel: 'First Name',
      fieldType: 'text',
      required: true,
      heading: 'Admin Details',
      initialValue: townshipAction?.ContactPhoneNumber || ''
    },
    {
      fieldName: 'AdminLastName',
      fieldLabel: 'Last Name',
      fieldType: 'text',
      required: true,
      initialValue: townshipAction?.ContactPhoneNumber || ''
    },
    {
      fieldName: 'AdminEmail',
      fieldLabel: 'Admin Email',
      fieldType: 'email',
      required: true,
      initialValue: townshipAction?.ContactPhoneNumber || ''
    },
    {
      fieldName: 'AdminContact',
      fieldLabel: 'Admin Contact',
      fieldType: 'mobile',
      required: true,
      initialValue: townshipAction?.ContactPhoneNumber || ''
    }
  ];

  let combinedFields =
    townshipAction === 'Add' ? allFields.concat(adminFields) : allFields;
  combinedFields =
    townshipAction !== 'Add' ? allFields.concat(statusField) : combinedFields;

    // Handle form submit
  async function handleSubmit(value: any) {
    let townshipData: any;
    if (townshipAction === 'Add') {
      townshipData = {
        TownshipName: value.TownshipName,
        State: value.State,
        County: value.County,
        Address: value.Address,
        City: value.City,
        ZipCode: value.ZipCode,
        ContactName: value.ContactName,
        ContactEmail: value.ContactEmail,
        ContactPhoneNumber: value.ContactPhoneNumber,
        AdminFirstName: value.AdminFirstName,
        AdminLastName: value.AdminLastName,
        AdminEmail: value.AdminEmail,
        AdminContactNumber: value.AdminContact,
        TownshipDescription: value.TownshipDescription || null,
        TownshipTagLine: value.TownshipTagLine || null
      };
    } else {
      townshipData = {
        Address: value.Address,
        ContactName: value.ContactName,
        ContactEmail: value.ContactEmail,
        ContactPhoneNumber: value.ContactPhoneNumber,
        Status: value.Status,
        TownshipDescription: value.TownshipDescription || null,
        TownshipTagLine: value.TownshipTagLine || null
      };
    }
    try {
      const response = await httpRequest(
        `${townshipAction === 'Add' ? 'POST' : 'PATCH'}`,
        `${
          townshipAction === 'Add'
            ? `/townships/add_township`
            : `/townships/${townshipAction.TownshipID}/update_township`
        }`,
        townshipData
      );

      if (response?.status === 'success') {
        toast.success(
          `Township ${
            townshipAction === 'Add' ? 'added' : 'details updated'
          } successfully!`
        );
        setTownshipAction('');
        handleApiCall();
      } else if (response?.type === 'NETWORK_ERROR') {
        toast.error('Network error. Please try again.');
      } else {
        toast.error(response?.message || 'Failed to add township.');
      }
    } catch (error) {
      toast.error('Failed to add township. Please try again.');
    }
  }

  // Handle cancel button
  function handleCancel() {
    setTownshipAction('');
  }

  return (
    <Card>
      <Box m={3}>
        <FormikForm
          allFields={combinedFields}
          buttonTitle={['Cancel', 'Save']}
          fullScreen={false}
          isEditModel={false}
          content={false}
          buttonPlacement={'right'}
          formMethod={handleSubmit}
          handleOtherButton={handleCancel}
        />
      </Box>
    </Card>
  );
};

export default AddTownship;
